import {
  getBirthdayState,
  getCustomerDetails,
  getCustomerSpend,
  getLegalPolicies,
  getVipProgram,
  join,
  setBirthday
} from '@theme/okendo-loyalty';

const CITY_ZONES = [
  ['Sydney', 'Australia/Sydney'],
  ['Melbourne', 'Australia/Melbourne'],
  ['Brisbane', 'Australia/Brisbane'],
  ['Perth', 'Australia/Perth'],
  ['Adelaide', 'Australia/Adelaide'],
  ['Hobart', 'Australia/Hobart'],
  ['Darwin', 'Australia/Darwin'],
  ['Auckland', 'Pacific/Auckland'],
  ['London', 'Europe/London'],
  ['Los Angeles', 'America/Los_Angeles'],
  ['New York', 'America/New_York']
];

/**
 * @param {string} value
 * @returns {string|undefined}
 */
function storeTimeZone(value) {
  const text = String(value || '');
  const slash = text.match(/[A-Za-z_]+\/[A-Za-z_]+/);
  if (slash) return slash[0];

  const known = CITY_ZONES.find(([city]) => text.includes(city));
  return known ? known[1] : undefined;
}

/**
 * @param {ParentNode} root
 * @returns {{ state: string, index: number, tierName: string, currency: string, timeZone: string, spendTemplate: string, expiryTemplate: string, tiers: { okendo: string, name: string, spend: string }[] }|null}
 */
function readConfig(root) {
  const node = root.querySelector('[data-resort-loyalty-config]');
  if (!(node instanceof HTMLScriptElement)) return null;

  try {
    const config = JSON.parse(node.textContent || '');
    if (!config || !Array.isArray(config.tiers)) return null;
    return config;
  } catch (error) {
    return null;
  }
}

/**
 * Okendo VIP tier entry spend. Public vip_program tiers use entryRequirement.spend (integer).
 * @param {unknown} tier
 * @returns {number|null}
 */
function entrySpend(tier) {
  if (!tier || typeof tier !== 'object' || !tier.entryRequirement || typeof tier.entryRequirement !== 'object') {
    return null;
  }
  const spend = tier.entryRequirement.spend;
  return typeof spend === 'number' && Number.isFinite(spend) ? spend : null;
}

/**
 * @param {unknown} program
 * @returns {object[]}
 */
function vipTiersFrom(program) {
  if (!program || typeof program !== 'object') return [];
  if (Array.isArray(program.vipTiers)) return program.vipTiers;
  if (program.vipProgram && Array.isArray(program.vipProgram.vipTiers)) return program.vipProgram.vipTiers;
  return [];
}

/**
 * Entry threshold from the public VIP program, matched by Okendo tier name.
 * @param {{ okendo?: string }} tier
 * @param {object[]} vipTiers
 * @returns {number|null}
 */
function thresholdFor(tier, vipTiers) {
  const name = String(tier && tier.okendo || '').trim().toLowerCase();
  if (!name) return null;
  const match = vipTiers.find((item) => String(item && item.name || '').trim().toLowerCase() === name);
  return entrySpend(match);
}

/**
 * @param {number} amount
 * @param {string} format
 * @param {string} currency
 * @returns {string}
 */
function formatMoney(amount, format, currency) {
  if (typeof format === 'string' && format.includes('{{')) {
    const fixed = amount.toFixed(2);
    const whole = String(Math.round(amount));
    const comma = (value) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount_no_decimals_with_comma_separator}}', comma(whole))
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{amount_with_comma_separator}}', comma(fixed))
      .replace('{{amount_with_apostrophe_separator}}', fixed.replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
      .replace('{{amount}}', fixed);
  }

  const digits = Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    return String(amount);
  }
}

/**
 * @param {string} value
 * @param {string} timeZone
 * @returns {string}
 */
function formatExpiry(value, timeZone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const options = { day: 'numeric', month: 'short', year: 'numeric' };
  const zone = storeTimeZone(timeZone);
  if (zone) options.timeZone = zone;

  return new Intl.DateTimeFormat('en-AU', options).format(date);
}

/**
 * @param {Element} element
 * @param {string} suffix
 * @returns {string}
 */
function classWithSuffix(element, suffix) {
  return [...element.classList].find((name) => name.endsWith(suffix)) || '';
}

/**
 * @param {ParentNode} root
 * @param {number} index
 * @param {number} progress
 */
function setTrack(root, index, progress) {
  const steps = [...root.querySelectorAll('[class]')].find((element) => classWithSuffix(element, '__track-steps'));
  if (!(steps instanceof HTMLElement)) return;

  const safeIndex = Math.max(0, index);
  const safeProgress = Math.min(100, Math.max(0, progress));
  steps.style.setProperty('--track-index', String(safeIndex));
  steps.style.setProperty('--track-progress', String(safeProgress));

  const noneClass = classWithSuffix(steps, '__track-steps--none');
  if (noneClass) steps.classList.remove(noneClass);

  const stepEls = [...steps.children].filter((element) => classWithSuffix(element, '__track-step'));
  stepEls.forEach((step, cursor) => {
    const dot = [...step.children].find((element) => classWithSuffix(element, '__track-dot'));
    const label = [...step.children].find((element) => classWithSuffix(element, '__track-label'));

    if (dot) {
      const filled = classWithSuffix(dot, '__track-dot--filled');
      const base = [...dot.classList].find((name) => name.endsWith('__track-dot') && !name.endsWith('--filled'));
      if (cursor <= safeIndex && !filled && base) dot.classList.add(`${base}--filled`);
      if (cursor > safeIndex && filled) dot.classList.remove(filled);
    }

    if (label) {
      const current = classWithSuffix(label, '__track-label--current');
      const future = classWithSuffix(label, '__track-label--future');
      const base = [...label.classList].find((name) => name.endsWith('__track-label') && !name.includes('--'));
      if (current) label.classList.remove(current);
      if (future) label.classList.remove(future);
      if (base && cursor === safeIndex) label.classList.add(`${base}--current`);
      if (base && cursor > safeIndex) label.classList.add(`${base}--future`);
    }
  });

  root.querySelectorAll('.sabo-resort__tier-card').forEach((card, cursor) => {
    card.classList.toggle('sabo-resort__tier-card--current', cursor === safeIndex);
  });
}

/**
 * @param {ParentNode} root
 * @param {number} index
 * @param {number} progress
 * @param {string} caption
 */
function showProgress(root, index, progress, caption) {
  setTrack(root, index, progress);
  const node = root.querySelector('[data-resort-progress]');
  if (!(node instanceof HTMLElement)) return;
  if (!caption) {
    node.hidden = true;
    return;
  }
  node.textContent = caption;
  node.hidden = false;
}

/**
 * @param {ParentNode} root
 */
function showEnrolled(root) {
  root.querySelectorAll('[data-resort-pending]').forEach((element) => {
    if (element instanceof HTMLElement) element.hidden = true;
  });
  root.querySelectorAll('[data-resort-enrolled], [data-resort-perks]').forEach((element) => {
    if (element instanceof HTMLElement) element.hidden = false;
  });
}

/**
 * @param {ParentNode} root
 * @param {string} tierName
 * @param {string} dateValue
 * @param {{ expiryTemplate?: string, timeZone?: string }} config
 */
function showExpiry(root, tierName, dateValue, config) {
  const node = root.querySelector('[data-resort-expiry]');
  if (!(node instanceof HTMLElement) || !tierName) return;

  const date = formatExpiry(dateValue, config.timeZone || '');
  if (!date) return;

  const template = config.expiryTemplate || 'Keep __TIER__ until __DATE__';
  node.textContent = template.replace('__TIER__', tierName).replace('__DATE__', date);
  node.hidden = false;
}

/**
 * @param {{ tiers: { okendo?: string, name?: string, spend?: string }[], currency?: string, moneyFormat?: string, spendTemplate?: string }} config
 * @param {object[]} vipTiers
 * @param {number} index
 * @param {number} spent
 * @returns {{ progress: number, caption: string }}
 */
function progressFromSpend(config, vipTiers, index, spent) {
  const nextTier = config.tiers[index + 1];
  const next = nextTier ? thresholdFor(nextTier, vipTiers) : null;
  if (next == null) return null;

  const current = thresholdFor(config.tiers[index], vipTiers);
  let progress = 0;
  if (current != null && next > current) {
    progress = ((spent - current) / (next - current)) * 100;
  } else if (next > 0) {
    progress = (spent / next) * 100;
  }

  const remaining = next - spent;
  if (remaining <= 0) return { progress: 100, caption: '' };

  const amount = formatMoney(remaining, config.moneyFormat || '', config.currency || '');
  const template = config.spendTemplate || 'Spend __AMOUNT__ more to reach __TIER__';
  return {
    progress,
    caption: template.replace('__AMOUNT__', amount).replace('__TIER__', nextTier.name || '')
  };
}

/**
 * @param {ParentNode} root
 * @param {{ set?: boolean }|null} birthday
 */
function showBirthday(root, birthday) {
  const form = root.querySelector('[data-resort-birthday]');
  const link = root.querySelector('[data-resort-birthday-link]');
  if (!birthday) return;

  if (birthday.set) {
    if (form instanceof HTMLElement) form.hidden = true;
    if (link instanceof HTMLElement) link.hidden = true;
    return;
  }

  if (link instanceof HTMLElement) link.hidden = true;
  if (form instanceof HTMLElement) form.hidden = false;
}

/**
 * @param {ParentNode} root
 */
async function refresh(root) {
  const config = readConfig(root);
  if (!config || root.getAttribute('data-customer') !== 'true') return;

  const [details, spendData, program, birthday] = await Promise.all([
    getCustomerDetails(),
    getCustomerSpend(),
    getVipProgram(),
    getBirthdayState()
  ]);

  const loyalty = details && details.loyaltyCustomer;
  const status = loyalty && loyalty.status;
  let index = Number(config.index);
  let tierName = config.tierName || '';
  const enrolled = status === 'enrolled' || config.state === 'enrolled';

  if (status === 'enrolled' && config.state !== 'enrolled') {
    index = 0;
    tierName = (config.tiers[0] && config.tiers[0].name) || tierName;
    showEnrolled(root);
  }

  if (enrolled && index >= 0 && config.tiers[index] && spendData && typeof spendData.customerSpend === 'number') {
    const result = progressFromSpend(config, vipTiersFrom(program), index, spendData.customerSpend);
    if (result) showProgress(root, index, result.progress, result.caption);
  }

  if (enrolled && loyalty && loyalty.dateTierExpires) {
    showExpiry(root, tierName, loyalty.dateTierExpires, config);
  }

  showBirthday(root, birthday);
}

/**
 * @param {MouseEvent} event
 */
async function onJoinClick(event) {
  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) return;

  const control = event.target instanceof Element ? event.target.closest('[data-resort-join]') : null;
  if (!(control instanceof HTMLElement) || !root.contains(control)) return;
  if (control.getAttribute('aria-busy') === 'true') return;

  event.preventDefault();
  control.setAttribute('aria-busy', 'true');

  const policies = await getLegalPolicies();
  const result = await join(policies);
  const loyalty = result && result.loyaltyCustomer;
  control.removeAttribute('aria-busy');

  if (loyalty && loyalty.status === 'enrolled') {
    const config = readConfig(root);
    showEnrolled(root);
    if (config) {
      const tierName = (config.tiers[0] && config.tiers[0].name) || '';
      const program = await getVipProgram();
      const spendData = await getCustomerSpend();
      if (spendData && typeof spendData.customerSpend === 'number') {
        const progress = progressFromSpend(config, vipTiersFrom(program), 0, spendData.customerSpend);
        if (progress) showProgress(root, 0, progress.progress, progress.caption);
        else setTrack(root, 0, 0);
      } else {
        setTrack(root, 0, 0);
      }
      const details = await getCustomerDetails();
      const enrolledCustomer = details && details.loyaltyCustomer;
      if (enrolledCustomer && enrolledCustomer.dateTierExpires) {
        showExpiry(root, tierName, enrolledCustomer.dateTierExpires, config);
      }
    }
    return;
  }

  if (control instanceof HTMLAnchorElement && control.href) {
    window.location.assign(control.href);
  }
}

/**
 * @param {SubmitEvent} event
 */
async function onBirthdaySubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-resort-birthday')) return;

  event.preventDefault();
  const day = Number(new FormData(form).get('day'));
  const month = Number(new FormData(form).get('month'));
  const saved = await setBirthday({ day, month });
  if (saved) form.hidden = true;
}

function boot() {
  document.querySelectorAll('[data-resort-loyalty]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    root.addEventListener('click', onJoinClick);
    root.addEventListener('submit', onBirthdaySubmit);
    refresh(root).catch(() => {});
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
